import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './Assignments.css';

const Assignments = () => {
  const { courseId } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [course, setCourse] = useState(null);
  const [submissions, setSubmissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isInstructor = user?.role === 'instructor' || user?.role === 'admin';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch course info
        const courseResponse = await api.get(`/courses/${courseId}`);
        setCourse(courseResponse.data);

        // Fetch assignments
        const assignmentsResponse = await api.get(`/assignments/course/${courseId}`);
        setAssignments(assignmentsResponse.data);

        // Fetch submissions for each assignment (for students)
        if (!isInstructor) {
          const submissionPromises = assignmentsResponse.data.map(async (assignment) => {
            try {
              const response = await api.get(`/assignments/${assignment.id}/my-submission`);
              return { assignmentId: assignment.id, submission: response.data };
            } catch (err) {
              return { assignmentId: assignment.id, submission: null };
            }
          });

          const submissionResults = await Promise.all(submissionPromises);
          const submissionsMap = {};
          submissionResults.forEach(({ assignmentId, submission }) => {
            submissionsMap[assignmentId] = submission;
          });
          setSubmissions(submissionsMap);
        }
      } catch (err) {
        console.error('Error fetching assignments:', err);
        setError(t('assignments.loadError'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [courseId, isInstructor]);

  const getStatusBadge = (assignment) => {
    const submission = submissions[assignment.id];

    if (!submission) {
      return <span className="status-badge pending">{t('assignments.notSubmitted')}</span>;
    }

    if (submission.status === 'graded') {
      return <span className="status-badge graded">{t('assignments.gradedBadge')}</span>;
    }

    return <span className="status-badge submitted">{t('assignments.submittedBadge')}</span>;
  };

  const getScore = (assignment) => {
    const submission = submissions[assignment.id];

    if (!submission || submission.status !== 'graded') {
      return null;
    }

    const percentage = Math.round((submission.score / assignment.max_score) * 100);
    return (
      <div className="score-display">
        <span className="score-value">{submission.score}</span>
        <span className="score-max">/ {assignment.max_score}</span>
        <span className={`score-percentage ${percentage >= 70 ? 'passing' : 'failing'}`}>
          ({percentage}%)
        </span>
      </div>
    );
  };

  const getDueDate = (assignment) => {
    if (!assignment.due_days) return null;

    // This would need enrollment date to calculate actual due date
    return (
      <span className="due-info">
        {t('assignments.due')}: {assignment.due_days} {t('assignments.daysAfterEnrollment')}
      </span>
    );
  };

  if (loading) return <div className="loading">{t('assignments.loading')}</div>;
  if (error) return <div className="error-state">{error}</div>;

  return (
    <div className="page assignments-page">
      <div className="container">
        <section className="assignments-hero">
          <div className="hero-content">
            <p className="eyebrow">{t('assignments.eyebrow')}</p>
            <h1>{course?.title || 'Assignments'}</h1>
            <p>
              {isInstructor
                ? t('assignments.instructorSubtitle')
                : t('assignments.studentSubtitle')}
            </p>
          </div>
          <div className="hero-card">
            <span className="label">{t('assignments.totalLabel')}</span>
            <p className="hero-value">{assignments.length}</p>
            {!isInstructor && (
              <p className="hero-note">
                {Object.values(submissions).filter(s => s?.status === 'graded').length} {t('assignments.gradedNote')}
              </p>
            )}
          </div>
        </section>

        {assignments.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p>{t('assignments.noAssignments')}</p>
            {isInstructor && (
              <Link to={`/edit-course/${courseId}`} className="btn-primary">
                {t('assignments.addAssignments')}
              </Link>
            )}
          </div>
        ) : (
          <div className="assignments-grid">
            {assignments.map((assignment) => (
              <article key={assignment.id} className="assignment-card">
                <header>
                  <div className="assignment-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="assignment-info">
                    {assignment.module_title && (
                      <p className="module-label">{assignment.module_title}</p>
                    )}
                    <h3>{assignment.title}</h3>
                  </div>
                  {!isInstructor && getStatusBadge(assignment)}
                </header>

                {assignment.description && (
                  <p className="assignment-description">{assignment.description}</p>
                )}

                <div className="assignment-meta">
                  <div className="meta-item">
                    <span className="meta-label">{t('assignments.maxScore')}</span>
                    <p>{assignment.max_score} {t('assignments.pointsLabel')}</p>
                  </div>
                  {assignment.due_days && (
                    <div className="meta-item">
                      <span className="meta-label">{t('assignments.due')}</span>
                      <p>{assignment.due_days} {t('assignments.daysAfterEnrollment')}</p>
                    </div>
                  )}
                  {assignment.allow_late && (
                    <div className="meta-item">
                      <span className="meta-label">{t('assignments.lateSubmissions')}</span>
                      <p>{t('assignments.allowed')}</p>
                    </div>
                  )}
                </div>

                {!isInstructor && getScore(assignment)}

                <div className="assignment-actions">
                  {isInstructor ? (
                    <Link
                      to={`/assignment/${assignment.id}/submissions`}
                      className="btn-primary"
                    >
                      {t('assignments.viewSubmissions')}
                    </Link>
                  ) : (
                    <Link
                      to={`/assignment/${assignment.id}/submit`}
                      className="btn-primary"
                    >
                      {submissions[assignment.id] ? t('assignments.viewSubmission') : t('assignments.submitAssignment')}
                    </Link>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="back-link-container">
          <Link to={`/courses/${courseId}`} className="back-link">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 19l-7-7 7-7" />
            </svg>
            {t('assignments.backToCourse')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Assignments;
